import ExcelJS from 'exceljs';
import {
  HOA_DON_MUA_DICH_VU_EXPORT_TITLE,
  HOA_DON_MUA_DICH_VU_FILE_NAME,
  HOA_DON_MUA_DICH_VU_SHEET_NAME
} from './constant';
import type { HoaDonMuaDichVu } from '@/types/schemas/hoa-don-mua-dich-vu.type';
import { fetchAllHoaDonMuaDichVu } from '@/hooks/queries/useHoaDonMuaDichVu';
import type { ExcelExportOptions, ExportFromApiOptions } from './types';
import { HOA_DON_MUA_DICH_VU_COLUMNS } from './column-mapping';

const buildFileName = (base: string) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${base}-${timestamp}.xlsx`;
};

const createWorkbook = (sheetName: string) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  return { workbook, worksheet };
};

const applyHeaderStyles = (row: ExcelJS.Row) => {
  row.eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } }
    };
  });
};

export const exportHoaDonMuaDichVuExcel = async (
  rows: HoaDonMuaDichVu[] = [],
  options?: Partial<ExcelExportOptions<HoaDonMuaDichVu>>
) => {
  const columns = options?.columns || HOA_DON_MUA_DICH_VU_COLUMNS;
  const title = options?.title || HOA_DON_MUA_DICH_VU_EXPORT_TITLE;
  const sheetName = options?.sheetName || HOA_DON_MUA_DICH_VU_SHEET_NAME;
  const fileName = buildFileName(options?.fileName || HOA_DON_MUA_DICH_VU_FILE_NAME);

  const { workbook, worksheet } = createWorkbook(sheetName);

  worksheet.mergeCells(1, 1, 1, columns.length);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.addRow([]);

  const headerRow = worksheet.addRow(columns.map(col => col.header));
  applyHeaderStyles(headerRow);

  rows.forEach(row => {
    const rowValues = columns.map(col => {
      const value = col.resolver(row);
      return value ?? '';
    });
    const excelRow = worksheet.addRow(rowValues);

    rowValues.forEach((value, colIndex) => {
      if (typeof value === 'number') {
        const cell = excelRow.getCell(colIndex + 1);
        cell.numFmt = '#,##0.00';
      }
    });
  });

  columns.forEach((col, index) => {
    const column = worksheet.getColumn(index + 1);
    column.width = col.width || 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
};

export const exportHoaDonMuaDichVuFromApi = async ({
  entitySlug,
  queryParams = {},
  pageSize = 200,
  excelOptions
}: ExportFromApiOptions) => {
  const rows = await fetchAllHoaDonMuaDichVu(entitySlug, queryParams, pageSize);
  return exportHoaDonMuaDichVuExcel(rows, excelOptions);
};
